import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ExternalLink, ChevronLeft, ChevronRight, RotateCcw, DownloadCloud,
} from 'lucide-react';
import { reportApi } from '@/api/report.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { SavedViewBar } from '@/components/reports/SavedViewBar';
import { AccountSearchInput } from '@/components/reports/AccountSearchInput';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { FinancialPeriodSelect } from '@/components/reports/FinancialPeriodSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import type { GlInquiryFilter, GlInquiryRow } from '@/types/report.types';
import type { PagedResult } from '@/types/api.types';

// ── helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().split('T')[0];
}
function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function fmtAmt(n: number) {
  if (n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso ?? '—';
  }
}
function fmtDateTime(iso: string | null) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return iso ?? '—';
  }
}

const PAGE_SIZE = 50;

const INPUT_CLS =
  'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500';
const LABEL_CLS = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const TH_CLS = 'px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap';
const TH_R_CLS = 'px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap';
const AMT_CLS = 'px-3 py-2 text-sm text-right tabular-nums';

const POSTING_STATUS_OPTIONS = [
  // 'All' (not '') on purpose: omitting the param makes the server apply its
  // Posted-only default; an explicit non-enum value disables the status filter.
  { value: 'All', label: 'All Statuses' },
  { value: 'Posted', label: 'Posted' },
  { value: 'NotPosted', label: 'Not Posted' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const WORKFLOW_STATUS_OPTIONS = [
  { value: '', label: 'All Workflow Statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'PendingApproval', label: 'Pending Approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Posted', label: 'Posted' },
];

const SOURCE_MODULE_OPTIONS = [
  { value: '', label: 'All Modules' },
  { value: 'AR', label: 'AR — Receivables' },
  { value: 'AP', label: 'AP — Payables' },
  { value: 'BNK', label: 'BNK — Banking' },
  { value: 'GL', label: 'GL — General Ledger' },
  { value: 'INTEGRATION', label: 'Integration' },
];

const REVERSAL_FILTER_OPTIONS = [
  { value: '', label: 'All Transactions' },
  { value: 'REVERSALS_ONLY', label: 'Reversals Only' },
  { value: 'EXCLUDE_REVERSALS', label: 'Exclude Reversals' },
];

type ColumnMode = 'COMPACT' | 'FULL';

// ── Status badges ──────────────────────────────────────────────────────────────

function PostingStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    Posted: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    NotPosted: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    Failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    Cancelled: 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  };
  return (
    <span className={cn(
      'inline-block px-1.5 py-0.5 rounded text-xs font-medium',
      styles[status] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
    )}>
      {status}
    </span>
  );
}

function WfStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    Draft: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    Submitted: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    PendingApproval: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    Approved: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
    Rejected: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    Cancelled: 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
    Posted: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  };
  return (
    <span className={cn(
      'inline-block px-1.5 py-0.5 rounded text-xs font-medium',
      styles[status] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
    )}>
      {status}
    </span>
  );
}

// ── Excel / CSV export ─────────────────────────────────────────────────────────

const COMPACT_COLUMNS: ExcelColumn[] = [
  { key: 'DocNumber', header: 'Doc Number', width: 18 },
  { key: 'DocDate', header: 'Doc Date', width: 14 },
  { key: 'PostDate', header: 'Post Date', width: 14 },
  { key: 'Type', header: 'Type', width: 10 },
  { key: 'AccountCode', header: 'Account Code', width: 14 },
  { key: 'AccountName', header: 'Account Name', width: 32 },
  { key: 'Narration', header: 'Narration / Reference', width: 36 },
  { key: 'Debit', header: 'Debit', width: 14, numFmt: '#,##0.00' },
  { key: 'Credit', header: 'Credit', width: 14, numFmt: '#,##0.00' },
  { key: 'Ccy', header: 'Ccy', width: 8 },
  { key: 'PostStatus', header: 'Post Status', width: 14 },
  { key: 'WfStatus', header: 'Wf Status', width: 16 },
  { key: 'Reversal', header: 'Rev', width: 6 },
];

const FULL_COLUMNS: ExcelColumn[] = [
  { key: 'DocNumber', header: 'Doc Number', width: 18 },
  { key: 'FY', header: 'FY', width: 10 },
  { key: 'Period', header: 'Period', width: 10 },
  { key: 'DocDate', header: 'Doc Date', width: 14 },
  { key: 'PostDate', header: 'Post Date', width: 14 },
  { key: 'Type', header: 'Type', width: 10 },
  { key: 'Line', header: 'Line', width: 6 },
  { key: 'AccountCode', header: 'Account Code', width: 14 },
  { key: 'AccountName', header: 'Account Name', width: 32 },
  { key: 'Narration', header: 'Narration / Reference', width: 36 },
  { key: 'Debit', header: 'Debit', width: 14, numFmt: '#,##0.00' },
  { key: 'Credit', header: 'Credit', width: 14, numFmt: '#,##0.00' },
  { key: 'Ccy', header: 'Ccy', width: 8 },
  { key: 'ExchRate', header: 'Exch Rate', width: 12, numFmt: '#,##0.000000' },
  { key: 'SourceModule', header: 'Source Module', width: 14 },
  { key: 'SourceDoc', header: 'Source Doc', width: 18 },
  { key: 'CreatedBy', header: 'Created By', width: 18 },
  { key: 'CreatedAt', header: 'Created At', width: 18 },
  { key: 'ApprovedBy', header: 'Approved By', width: 18 },
  { key: 'PostedBy', header: 'Posted By', width: 18 },
  { key: 'PostedAt', header: 'Posted At', width: 18 },
  { key: 'Branch', header: 'Branch', width: 12 },
  { key: 'CostCentre', header: 'Cost Centre', width: 12 },
  { key: 'PostStatus', header: 'Post Status', width: 14 },
  { key: 'WfStatus', header: 'Wf Status', width: 16 },
  { key: 'ReversalStatus', header: 'Reversal Status', width: 14 },
  { key: 'SysGen', header: 'System Generated', width: 14 },
];

function reversalStatusText(r: GlInquiryRow): string {
  return r.isReversal ? 'REVERSAL' : r.isReversed ? 'REVERSED' : '';
}

function buildExportRows(items: GlInquiryRow[], mode: ColumnMode): Record<string, unknown>[] {
  return items.map(r => {
    const base: Record<string, unknown> = {
      DocNumber: r.documentNumber,
      DocDate: r.documentDate,
      PostDate: r.postingDate,
      Type: r.transactionTypeCode,
      AccountCode: r.accountCode ?? '',
      AccountName: r.accountName ?? '',
      Narration: r.narration ?? r.reference ?? '',
      Debit: r.debit,
      Credit: r.credit,
      Ccy: r.currencyCode,
      PostStatus: r.postingStatus,
      WfStatus: r.workflowStatus,
    };
    if (mode === 'COMPACT') {
      base.Reversal = r.isReversal ? 'Yes' : '';
      return base;
    }
    return {
      ...base,
      FY: r.financialYearCode ?? '',
      Period: r.financialPeriodCode ?? '',
      Line: r.lineNumber,
      ExchRate: r.exchangeRate,
      SourceModule: r.sourceModule ?? '',
      SourceDoc: r.sourceDocumentNo ?? '',
      CreatedBy: r.createdByName ?? '',
      CreatedAt: r.createdDate ?? '',
      ApprovedBy: r.approvedByName ?? '',
      PostedBy: r.postedByName ?? '',
      PostedAt: r.postedDate ?? '',
      Branch: r.locationCode ?? '',
      CostCentre: r.costCentreCode ?? '',
      ReversalStatus: reversalStatusText(r),
      SysGen: r.isSystemGenerated ? 'Yes' : 'No',
    };
  });
}

// ── Stat item ─────────────────────────────────────────────────────────────────

function SummaryItem({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-gray-500 dark:text-gray-400">{label}:</span>
      <span className="font-semibold text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

const DEFAULT_FILTER: GlInquiryFilter = {
  fromDate: firstOfMonth(),
  toDate: today(),
  pageNumber: 1,
  pageSize: PAGE_SIZE,
};

export const GlInquiryPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<GlInquiryFilter>({ ...DEFAULT_FILTER });
  const [accountId, setAccountId] = useState<string>('');
  const [accountLabel, setAccountLabel] = useState<string>('');
  const [columnMode, setColumnMode] = useState<ColumnMode>('COMPACT');
  const [result, setResult] = useState<PagedResult<GlInquiryRow> | null>(null);
  const [loading, setLoading] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const canExportAll = hasPermission(Permissions.RPT_EXPORT_DATA);

  const buildQuery = useCallback((page: number): GlInquiryFilter => ({
    ...filter,
    accountId: accountId || undefined,
    pageNumber: page,
  }), [filter, accountId]);

  const run = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportApi.getGlInquiry(buildQuery(page));
      setResult(res.data?.data ?? null);
      setFilter(f => ({ ...f, pageNumber: page }));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load GL inquiry');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const resetFilter = () => {
    setFilter({ ...DEFAULT_FILTER });
    setAccountId('');
    setAccountLabel('');
    setResult(null);
    setError(null);
  };

  if (!hasPermission(Permissions.RPT_LEDGER)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const totalPages = result ? Math.ceil(result.totalCount / PAGE_SIZE) : 0;
  const currentPage = filter.pageNumber ?? 1;
  const sumDebits = result?.items.reduce((s, r) => s + r.debit, 0) ?? 0;
  const sumCredits = result?.items.reduce((s, r) => s + r.credit, 0) ?? 0;
  const full = columnMode === 'FULL';
  const columnCount = full ? 27 : 13;
  const filterSummary = `${filter.fromDate}..${filter.toDate}${filter.sourceModule ? ` · ${filter.sourceModule}` : ''}${filter.reversalFilter ? ` · ${filter.reversalFilter}` : ''}`;

  const handleExcel = () => {
    if (!result) return;
    exportToExcel({
      sheetName: 'GL Transaction Detail',
      columns: full ? FULL_COLUMNS : COMPACT_COLUMNS,
      rows: buildExportRows(result.items, columnMode),
      title: 'GL Transaction Detail',
      subtitle: `${filter.fromDate} to ${filter.toDate}`,
    }, `GlTransactionDetail_${filter.fromDate}_${filter.toDate}`);
  };

  const handleCsv = () => {
    if (!result) return;
    exportToCsvRows(
      full ? FULL_COLUMNS : COMPACT_COLUMNS,
      buildExportRows(result.items, columnMode),
      `GlTransactionDetail_${filter.fromDate}_${filter.toDate}_p${currentPage}`,
    );
  };

  /**
   * Server-streamed CSV of the WHOLE filtered result set. The apiClient
   * interceptor injects the JWT, so the file is fetched as a blob and saved
   * via an object URL rather than a plain (unauthenticated) anchor navigation.
   */
  const handleExportAll = async () => {
    setExportingAll(true);
    setError(null);
    try {
      const { pageNumber: _p, pageSize: _s, ...rest } = buildQuery(1);
      void _p; void _s;
      const res = await reportApi.exportGlInquiryCsv(rest as GlInquiryFilter);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gl-transaction-detail_${filter.fromDate}_${filter.toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to export CSV');
    } finally {
      setExportingAll(false);
    }
  };

  const applySavedView = (f: unknown) => {
    if (!f || typeof f !== 'object') return;
    const obj = f as Partial<GlInquiryFilter> & {
      columnMode?: ColumnMode; accountId?: string; accountLabel?: string;
    };
    const { columnMode: savedMode, accountId: savedAccount, accountLabel: savedLabel, ...rest } = obj;
    if (savedMode === 'COMPACT' || savedMode === 'FULL') setColumnMode(savedMode);
    if (savedAccount !== undefined) setAccountId(savedAccount ?? '');
    if (savedLabel !== undefined) setAccountLabel(savedLabel ?? '');
    setFilter(prev => ({ ...prev, ...rest, pageNumber: 1 }));
  };

  const handlePrint = () => setShowPrint(true);

  return (
    <div className="flex flex-col h-full gap-4 p-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Search size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">GL Transaction Detail</h1>
          {/* Column mode toggle */}
          <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden ml-2">
            <button
              onClick={() => setColumnMode('COMPACT')}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                !full ? 'bg-brand-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
              title="Compact columns"
            >
              Compact
            </button>
            <button
              onClick={() => setColumnMode('FULL')}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                full ? 'bg-brand-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
              title="Full detail columns"
            >
              Full detail
            </button>
          </div>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          onPrint={handlePrint}
          hasData={!!result}
          reportKey="GL_TRANSACTION_DETAIL"
          rowCount={result?.items.length ?? 0}
          filterSummary={filterSummary}
        />
      </div>

      {/* Saved views */}
      <SavedViewBar
        reportKey="GL_TRANSACTION_DETAIL"
        currentFilters={{ ...filter, accountId, accountLabel, columnMode }}
        onApply={applySavedView}
      />

      {/* Filter Panel */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        {/* Row 1 — Dates + quick period */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Quick Period</label>
            <FinancialPeriodSelect
              onPeriodChange={(from, to) => {
                setFilter(f => ({ ...f, fromDate: from, toDate: to }));
              }}
            />
          </div>
        </div>

        {/* Row 2 — Doc Number, Tx Type, Posting Status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Document Number</label>
            <input
              type="text"
              value={filter.documentNumber ?? ''}
              placeholder="Search doc number…"
              onChange={e => setFilter(f => ({ ...f, documentNumber: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Transaction Type</label>
            <input
              type="text"
              value={filter.transactionTypeCode ?? ''}
              placeholder="e.g. JV, AP, AR…"
              onChange={e => setFilter(f => ({ ...f, transactionTypeCode: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Posting Status</label>
            <select
              value={filter.postingStatus ?? ''}
              onChange={e => setFilter(f => ({ ...f, postingStatus: e.target.value || undefined }))}
              className={INPUT_CLS}
            >
              {POSTING_STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 3 — Account, Location, Cost Centre */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Account</label>
            <AccountSearchInput
              accountId={accountId}
              accountLabel={accountLabel}
              onChange={(id, code, name) => {
                setAccountId(id);
                setAccountLabel(`${code} — ${name}`);
              }}
              onClear={() => {
                setAccountId('');
                setAccountLabel('');
              }}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id ?? undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id ?? undefined }))}
            />
          </div>
        </div>

        {/* Row 4 — Narration, Reference, Workflow Status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Narration</label>
            <input
              type="text"
              value={filter.narration ?? ''}
              placeholder="Search narration…"
              onChange={e => setFilter(f => ({ ...f, narration: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Reference Contains</label>
            <input
              type="text"
              value={filter.reference ?? ''}
              placeholder="Search reference…"
              onChange={e => setFilter(f => ({ ...f, reference: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Workflow Status</label>
            <select
              value={filter.workflowStatus ?? ''}
              onChange={e => setFilter(f => ({ ...f, workflowStatus: e.target.value || undefined }))}
              className={INPUT_CLS}
            >
              {WORKFLOW_STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 5 — Source module, Reversals, Amount range */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Source Module</label>
            <select
              value={filter.sourceModule ?? ''}
              onChange={e => setFilter(f => ({ ...f, sourceModule: e.target.value || undefined }))}
              className={INPUT_CLS}
            >
              {SOURCE_MODULE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Reversals</label>
            <select
              value={filter.reversalFilter ?? ''}
              onChange={e => setFilter(f => ({ ...f, reversalFilter: e.target.value || undefined }))}
              className={INPUT_CLS}
            >
              {REVERSAL_FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Amount Min</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={filter.amountMin ?? ''}
              placeholder="0.00"
              onChange={e => setFilter(f => ({ ...f, amountMin: e.target.value === '' ? undefined : Number(e.target.value) }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Amount Max</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={filter.amountMax ?? ''}
              placeholder="No limit"
              onChange={e => setFilter(f => ({ ...f, amountMax: e.target.value === '' ? undefined : Number(e.target.value) }))}
              className={INPUT_CLS}
            />
          </div>
        </div>

        {/* Row 6 — Account code range */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Account Code From</label>
            <input
              type="text"
              value={filter.accountCodeFrom ?? ''}
              placeholder="e.g. 1000"
              onChange={e => setFilter(f => ({ ...f, accountCodeFrom: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Account Code To</label>
            <input
              type="text"
              value={filter.accountCodeTo ?? ''}
              placeholder="e.g. 9999"
              onChange={e => setFilter(f => ({ ...f, accountCodeTo: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <RunReportButton
            label="Run Query"
            onClick={() => run(1)}
            isLoading={loading}
            disabled={!filter.fromDate || !filter.toDate}
          />
          <button
            onClick={resetFilter}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <RotateCcw size={14} />
            Reset
          </button>
          {canExportAll && (
            <button
              onClick={() => { void handleExportAll(); }}
              disabled={exportingAll || !filter.fromDate || !filter.toDate}
              className="flex items-center gap-2 px-3 py-2 border border-teal-600 text-teal-700 dark:text-teal-400 rounded-lg text-sm hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export the entire filtered result set as CSV (server-side)"
            >
              {exportingAll ? <Spinner size="sm" /> : <DownloadCloud size={14} />}
              Export all (CSV)
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col min-h-0 flex-1">
          {/* Summary bar */}
          <div className="flex flex-wrap items-center gap-4 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <SummaryItem
              label="Total records"
              value={result.totalCount.toLocaleString()}
            />
            {result.totalCount > 0 && (
              <SummaryItem label="Page" value={`${currentPage} of ${totalPages}`} />
            )}
            <SummaryItem label="Sum of Debits" value={fmtAmt(sumDebits)} />
            <SummaryItem label="Sum of Credits" value={fmtAmt(sumCredits)} />
            {loading && <Spinner size="sm" />}
          </div>

          {/* Print area — horizontal scroll container */}
          <div data-print-section id="report-print-area" className="overflow-auto flex-1">
            <table className={cn('w-full text-sm border-collapse', full ? 'min-w-[2600px]' : 'min-w-[1200px]')}>
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                <tr>
                  <th className={cn(TH_CLS, 'w-32')}>Doc Number</th>
                  {full && <th className={cn(TH_CLS, 'w-20')}>FY</th>}
                  {full && <th className={cn(TH_CLS, 'w-20')}>Period</th>}
                  <th className={cn(TH_CLS, 'w-24')}>Doc Date</th>
                  <th className={cn(TH_CLS, 'w-24')}>Post Date</th>
                  <th className={cn(TH_CLS, 'w-16')}>Type</th>
                  {full && <th className={cn(TH_R_CLS, 'w-14')}>Line</th>}
                  <th className={cn(TH_CLS, 'w-24')}>Acct Code</th>
                  <th className={TH_CLS}>Account Name</th>
                  <th className={TH_CLS}>Narration / Ref</th>
                  <th className={cn(TH_R_CLS, 'w-28')}>Debit</th>
                  <th className={cn(TH_R_CLS, 'w-28')}>Credit</th>
                  <th className={cn(TH_CLS, 'w-12')}>Ccy</th>
                  {full && <th className={cn(TH_R_CLS, 'w-24')}>Exch Rate</th>}
                  {full && <th className={cn(TH_CLS, 'w-24')}>Source Module</th>}
                  {full && <th className={cn(TH_CLS, 'w-28')}>Source Doc</th>}
                  {full && <th className={cn(TH_CLS, 'w-28')}>Created By</th>}
                  {full && <th className={cn(TH_CLS, 'w-32')}>Created At</th>}
                  {full && <th className={cn(TH_CLS, 'w-28')}>Approved By</th>}
                  {full && <th className={cn(TH_CLS, 'w-28')}>Posted By</th>}
                  {full && <th className={cn(TH_CLS, 'w-32')}>Posted At</th>}
                  {full && <th className={cn(TH_CLS, 'w-20')}>Branch</th>}
                  {full && <th className={cn(TH_CLS, 'w-24')}>Cost Centre</th>}
                  <th className={cn(TH_CLS, 'w-24')}>Post Status</th>
                  <th className={cn(TH_CLS, 'w-28')}>Wf Status</th>
                  {full ? (
                    <>
                      <th className={cn(TH_CLS, 'w-24')}>Reversal</th>
                      <th className={cn(TH_CLS, 'w-16')}>Sys</th>
                    </>
                  ) : (
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-10">Rev</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {result.items.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                      No records found for the selected criteria.
                    </td>
                  </tr>
                ) : (
                  result.items.map((row, idx) => (
                    <tr
                      key={`${row.journalId}-${idx}`}
                      className={cn(
                        'border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50',
                        row.isReversal && 'bg-orange-50/60 dark:bg-orange-900/10'
                      )}
                    >
                      <td className="px-3 py-2 text-sm">
                        <button
                          onClick={() => navigate(AppRoutes.GL_JOURNAL_DETAIL.replace(':id', row.journalId))}
                          className="text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1 font-medium"
                        >
                          {row.documentNumber}
                          <ExternalLink size={11} />
                        </button>
                      </td>
                      {full && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.financialYearCode ?? '—'}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.financialPeriodCode ?? '—'}</td>}
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtDate(row.documentDate)}</td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtDate(row.postingDate)}</td>
                      <td className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">{row.transactionTypeCode}</td>
                      {full && <td className={cn(AMT_CLS, 'text-xs text-gray-500 dark:text-gray-400')}>{row.lineNumber || '—'}</td>}
                      <td className="px-3 py-2 text-xs font-mono text-gray-500 dark:text-gray-400">{row.accountCode ?? '—'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 max-w-[160px] truncate" title={row.accountName ?? ''}>
                        {row.accountName ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 max-w-[200px] truncate" title={row.narration ?? row.reference ?? ''}>
                        {row.narration ?? row.reference ?? '—'}
                      </td>
                      <td className={cn(AMT_CLS, 'text-gray-900 dark:text-gray-100')}>{fmtAmt(row.debit)}</td>
                      <td className={cn(AMT_CLS, 'text-gray-900 dark:text-gray-100')}>{fmtAmt(row.credit)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.currencyCode}</td>
                      {full && (
                        <td className={cn(AMT_CLS, 'text-xs text-gray-500 dark:text-gray-400')}>
                          {row.exchangeRate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                        </td>
                      )}
                      {full && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.sourceModule ?? '—'}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.sourceDocumentNo ?? '—'}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{row.createdByName ?? '—'}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDateTime(row.createdDate)}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{row.approvedByName ?? '—'}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{row.postedByName ?? '—'}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDateTime(row.postedDate)}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.locationCode ?? '—'}</td>}
                      {full && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.costCentreCode ?? '—'}</td>}
                      <td className="px-3 py-2"><PostingStatusBadge status={row.postingStatus} /></td>
                      <td className="px-3 py-2"><WfStatusBadge status={row.workflowStatus} /></td>
                      {full ? (
                        <>
                          <td className="px-3 py-2 text-xs">
                            {row.isReversal ? (
                              <span className="text-orange-600 dark:text-orange-400 font-medium">REVERSAL</span>
                            ) : row.isReversed ? (
                              <span className="text-amber-600 dark:text-amber-400 font-medium">REVERSED</span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-600">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                            {row.isSystemGenerated ? 'Yes' : 'No'}
                          </td>
                        </>
                      ) : (
                        <td className="px-3 py-2 text-center">
                          {row.isReversal && (
                            <span
                              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
                              title="Reversal"
                            >
                              <RotateCcw size={10} />
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
              {result.items.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                    <td
                      colSpan={full ? 10 : 7}
                      className="px-3 py-2 text-sm font-bold text-gray-900 dark:text-white"
                    >
                      PAGE TOTAL ({result.items.length.toLocaleString()} of {result.totalCount.toLocaleString()} records)
                    </td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(sumDebits)}</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(sumCredits)}</td>
                    <td colSpan={full ? 15 : 4}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, result.totalCount)} of {result.totalCount.toLocaleString()}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => run(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  className="p-1.5 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page = i + 1;
                  if (totalPages > 5) {
                    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                    page = start + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => run(page)}
                      disabled={loading}
                      className={cn(
                        'w-7 h-7 rounded text-xs font-medium',
                        page === currentPage
                          ? 'bg-brand-600 text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                      )}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => run(currentPage + 1)}
                  disabled={currentPage >= totalPages || loading}
                  className="p-1.5 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!result && !loading && (
        <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
          Set filters and click "Run Query" to search GL transactions.
        </div>
      )}

      {/* ── Print Preview ─────────────────────────────────────────────────── */}
      {result && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="GL Transaction Detail"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="GL Transaction Detail"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Period',   value: `${fmtDate(filter.fromDate)} to ${fmtDate(filter.toDate)}` },
              { label: 'Records',  value: `${result.items.length} shown (of ${result.totalCount} total)` },
              ...(accountLabel ? [{ label: 'Account', value: accountLabel }] : []),
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Doc Number','Doc Date','Post Date','Type','Acct Code','Account Name','Narration / Ref','Debit','Credit','Ccy','Post Status','Wf Status'].map((h, i) => (
                  <th key={h} style={{ background: '#1e3a5f', color: 'white', padding: '4px 5px', textAlign: i >= 7 && i <= 8 ? 'right' : 'left', fontWeight: 700, whiteSpace: 'nowrap', fontSize: '8px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.items.map((row: GlInquiryRow, idx: number) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', background: row.isReversal ? '#fff7ed' : undefined }}>
                  <td style={{ padding: '3px 5px', fontFamily: 'monospace', fontWeight: 600 }}>{row.documentNumber}</td>
                  <td style={{ padding: '3px 5px', whiteSpace: 'nowrap' }}>{fmtDate(row.documentDate)}</td>
                  <td style={{ padding: '3px 5px', whiteSpace: 'nowrap' }}>{fmtDate(row.postingDate)}</td>
                  <td style={{ padding: '3px 5px', color: '#6b7280' }}>{row.transactionTypeCode}</td>
                  <td style={{ padding: '3px 5px', fontFamily: 'monospace', color: '#6b7280' }}>{row.accountCode ?? '—'}</td>
                  <td style={{ padding: '3px 5px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.accountName ?? '—'}</td>
                  <td style={{ padding: '3px 5px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>{row.narration ?? row.reference ?? '—'}</td>
                  <td style={{ padding: '3px 5px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(row.debit)}</td>
                  <td style={{ padding: '3px 5px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(row.credit)}</td>
                  <td style={{ padding: '3px 5px', color: '#6b7280' }}>{row.currencyCode}</td>
                  <td style={{ padding: '3px 5px', color: row.postingStatus === 'Posted' ? '#16a34a' : '#6b7280' }}>{row.postingStatus}</td>
                  <td style={{ padding: '3px 5px', color: '#6b7280' }}>{row.workflowStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
